// Subscription state in the profile and the payment flow — the same model as
// the production bot:
// • 5-day Plus trial, once per account;
// • «Сменить тариф» with a paid plan annuls the current one first (no refund);
// • payment by card (CloudPayments, CIS) or crypto (NOWPayments page), then
//   «Проверить оплату»: paid / not confirmed yet / failed;
// • when a plan or the trial ends the account moves to Free with standard settings.
// ORCA API: the payment is created for the session's account and paid on the
// provider's page; only the provider's webhook makes it paid — the return to
// /profile?payment=<id> just asks the server (usePaymentReturn).
import { useTime } from '@/hooks/useTime';
import { useEffect, useState, type CSSProperties } from 'react';
import { useToast } from '@/app/providers/Toast';
import { XButton } from '@/components/ui/Buttons';
import { Icon } from '@/components/ui/Icon';
import { useSheet } from '@/components/ui/Sheet';
import { usePlanCatalog, usePlans, useProfile, useProfileAction } from '@/hooks/queries';
import { useComparePlans } from '@/features/plans/Compare';
import { profileApi } from '@/lib/api/client';
import { MOCK_DATA } from '@/lib/api/mode';
import i18n, { useT } from '@/i18n';
import { cx, nf, price as fmtPrice } from '@/lib/format';
import type { Profile } from '@/types';
import { Act } from './Sections';

type Method = 'card' | 'crypto';
const METHODS: Array<{ id: Method; icon: string; provider: string }> = [
  { id: 'card', icon: 'card', provider: 'CloudPayments' },
  { id: 'crypto', icon: 'globe', provider: 'NOWPayments' },
];
const monthsWord = (n: number) => i18n.t('profile:pay.months', { count: n });
/** USDT amount for the crypto provider: the same number, in the UI's number format */
const usdt = (n: number) => `${nf(n, Number.isInteger(n) ? 0 : 2)} USDT`;
type Mode = 'new' | 'renew' | 'change';
type Start = { mode: Mode; plan?: 'plus' | 'premium' };

export function useOpenPayment() {
  const { openSheet } = useSheet();
  return (o: Start) => openSheet({
    kicker: i18n.t('profile:pay.kicker'),
    title: i18n.t(o.mode === 'renew' ? 'profile:pay.renewTitle' : o.mode === 'change' ? 'profile:pay.changeTitle' : 'profile:pay.newTitle'),
    body: <PaymentFlow start={o} />,
    className: 'pay-sheet',
  });
}

/* ---------- subscription card ---------- */
const TONE: Record<Profile['subscription']['status'], string> = { active: 'ok', trial: 'trial', expiring: 'warn', free: 'free', paused: 'paused', pending: 'wait', failed: 'bad' };
/** «Что входит в …» from the plan catalog + the way to the full comparison */
function PlanFeatures({ id }: { id: 'free' | 'plus' | 'premium' }) {
  const { t } = useT('plans');
  const cat = usePlanCatalog();
  const compare = useComparePlans();
  const plan = cat.plans.find((p) => p.id === id);
  if (!plan) return null;
  return (
    <div className="pay-feats">
      <span className="up-plan-k">{t('up.includes', { plan: plan.name })}</span>
      <ul className="up-feats">{plan.features.map((f) => <li key={f}>{f}</li>)}</ul>
      <button type="button" className="up-cmp" onClick={compare}><Icon name="link" />{t('up.compare')}</button>
    </div>
  );
}

function TrialOffer({ used }: { used: boolean }) {
  const { t } = useT('profile');
  const toast = useToast();
  const start = useProfileAction(profileApi.startTrial);
  if (used) return <p className="sub-note"><Icon name="check" />{t('trial.used')}</p>;
  return (
    <button type="button" className="trial-cta" disabled={start.pending}
      onClick={async () => { try { await start.run(); toast(t('trial.on')); } catch (e) { toast((e as Error).message); } }}>
      <span className="trial-ic"><Icon name="plus" /></span>
      <span><b>{start.pending ? t('plans:up.activating') : t('plans:cta.trial', { count: 5 })}</b><small>{t('trial.note')}</small></span>
      <Icon name="chev" />
    </button>
  );
}

export function SubscriptionCard({ p }: { p: Profile }) {
  const { t } = useT('profile');
  const tf = useTime();
  const ends = (v: string | null) => (v ? tf.dateLong(v) : '—');
  const sub = p.subscription;
  const pay = useOpenPayment();
  const toast = useToast();
  const resume = useProfileAction(profileApi.resume);
  const ack = useProfileAction(profileApi.ackDowngrade);
  const check = useProfileAction(profileApi.checkPayment);
  const st = { label: t(`sub.status.${sub.status}`), tone: TONE[sub.status] };
  const ring = sub.days ? Math.max(0, Math.min(1, sub.daysLeft / sub.days)) : sub.status === 'free' ? 1 : 0;
  const paid = sub.plan === 'Plus' ? 'plus' : 'premium';

  let body = null;
  switch (sub.status) {
    case 'free':
      body = sub.downgrade ? (
        <div className="sub-msg free">
          <b>{sub.downgrade.reason === 'trial' ? t('sub.trialEnded') : t('sub.paidEnded', { plan: sub.downgrade.from })}</b>
          <p>{t('sub.downText')}</p>
          <div className="sub-acts">
            <XButton inv label={t('sub.backPlus')} onClick={() => pay({ mode: 'new', plan: 'plus' })} />
            <XButton inv label={t('plans:cta.seePremium')} onClick={() => pay({ mode: 'new', plan: 'premium' })} />
            <button type="button" className="stc-link" onClick={() => ack.run()}>{t('common:action.understood')}</button>
          </div>
        </div>
      ) : (
        <div className="sub-msg free">
          <b>{t('sub.freeTitle', { price: fmtPrice(0) })}</b>
          <PlanFeatures id="free" />
          <p>{t('sub.freeMore')}</p>
          <TrialOffer used={sub.trialUsed} />
          <div className="sub-acts"><XButton inv label="Plus" onClick={() => pay({ mode: 'new', plan: 'plus' })} /><XButton inv label="Premium" onClick={() => pay({ mode: 'new', plan: 'premium' })} /></div>
        </div>
      );
      break;
    case 'paused':
      body = (
        <div className="sub-msg paused">
          <b>{t('sub.pausedTitle')}</b>
          <p>{MOCK_DATA ? t('sub.pausedText') : t('sub.pausedTextTg')}</p>
          {MOCK_DATA
            ? <XButton inv label={resume.pending ? t('sub.resuming') : t('sub.resume')} busy={resume.pending} onClick={async () => { await resume.run(); toast(t('sub.resumed')); }} />
            : <a className="pf-act tg" href="https://t.me/Orcafamily_bot" target="_blank" rel="noreferrer"><Icon name="tg" />{t('sub.openBot')}</a>}
        </div>
      );
      break;
    case 'trial':
      body = (
        <div className="sub-msg trial">
          <b>{t('sub.trialLeft', { count: sub.daysLeft })}</b>
          <p>{t('sub.trialText', { date: ends(sub.endsAt) })}</p>
          <div className="sub-acts"><XButton inv label={t('plans:cta.subscribe', { plan: 'Plus' })} onClick={() => pay({ mode: 'new', plan: 'plus' })} /><XButton inv label="Premium" onClick={() => pay({ mode: 'new', plan: 'premium' })} /></div>
        </div>
      );
      break;
    case 'expiring':
      body = (
        <div className="sub-msg warn">
          <b>{t('sub.expiring', { plan: sub.plan, count: sub.daysLeft, date: ends(sub.endsAt) })}</b>
          <p>{t('sub.expiringText')}</p>
          <XButton inv label={t('sub.renew', { plan: sub.plan })} onClick={() => pay({ mode: 'renew', plan: paid })} />
        </div>
      );
      break;
    case 'pending':
      body = (
        <div className="sub-msg wait">
          <b><span className="pf-spin" />{t('sub.pendingTitle')}</b>
          <p>{sub.pending?.amount} · {sub.pending?.method} · {t('sub.pendingText', { time: sub.pending ? `${tf.time(sub.pending.since)} ${tf.zone}` : '—' })}</p>
          <div className="sub-acts">
            <Act busy={check.pending} onClick={async () => {
              try { await check.run(sub.pending?.id); toast(t('sub.paidToast')); } catch (e) { toast((e as Error).message); }
            }}>{t('pay.check')}</Act>
          </div>
        </div>
      );
      break;
    case 'failed':
      body = (
        <div className="sub-msg bad">
          <b>{t('pay.failedTitle')}</b>
          <p>{t('sub.failedText', { plan: sub.plan, date: ends(sub.endsAt) })}</p>
          <div className="sub-acts"><XButton inv label={t('sub.payAgain')} onClick={() => pay({ mode: 'renew', plan: paid })} /></div>
        </div>
      );
      break;
    default:
      body = null;
  }

  return (
    <div className={cx('sub', sub.status)}>
      <div className="sub-top">
        <span className="sub-ring" style={{ '--p': ring } as CSSProperties} aria-hidden="true"><svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="17" /><circle className="v" cx="20" cy="20" r="17" pathLength={1} /></svg><b>{sub.status === 'paused' ? '⏸' : sub.status === 'free' ? '∞' : sub.daysLeft}</b></span>
        <div className="sub-t">
          <span className={cx('sub-badge', st.tone)}><i />{st.label}</span>
          <b>{sub.plan}{sub.plan !== 'Free' ? ` · ${sub.period}` : ''}</b>
          <small>{sub.status === 'paused' ? t('sub.pausedSmall') : sub.plan === 'Free' ? t('sub.freeSmall') : t('sub.untilSmall', { date: ends(sub.endsAt), count: sub.daysLeft })}</small>
        </div>
      </div>
      {body}
      {/* a payment that was started and not confirmed yet (the provider decides, not this page) */}
      {sub.pending && sub.status !== 'pending' && (
        <div className="sub-msg wait">
          <b><span className="pf-spin" />{t('sub.pendingTitle')}</b>
          <p>{sub.pending.amount} · {sub.pending.method} · {t('sub.pendingText', { time: `${tf.time(sub.pending.since)} ${tf.zone}` })}</p>
          <div className="sub-acts">
            <Act busy={check.pending} onClick={async () => {
              try { await check.run(sub.pending?.id); toast(t('sub.paidToast')); } catch (e) { toast((e as Error).message); }
            }}>{t('pay.check')}</Act>
          </div>
        </div>
      )}
      {sub.status === 'active' && (
        <div className="sub-acts">
          <XButton label={t('sub.renew', { plan: sub.plan })} inv sm onClick={() => pay({ mode: 'renew', plan: paid })} />
          <Act onClick={() => pay({ mode: 'change' })}>{t('sub.change')}</Act>
        </div>
      )}
    </div>
  );
}

/* ---------- payment flow ---------- */
type Step = 'warn' | 'plan' | 'period' | 'method' | 'redirect' | 'checking' | 'pending' | 'done' | 'failed' | 'trial';

function PaymentFlow({ start }: { start: Start }) {
  const { t } = useT('profile');
  const tf = useTime();
  const ends = (v: string | null) => (v ? tf.dateLong(v) : '—');
  const plans = usePlans();
  const p = useProfile();
  const { closeSheet } = useSheet();
  const toast = useToast();
  const cancel = useProfileAction(profileApi.cancelPlan);
  const check = useProfileAction(profileApi.checkPayment);
  const trial = useProfileAction(profileApi.startTrial);
  const [planId, setPlanId] = useState<'plus' | 'premium'>(start.plan ?? 'premium');
  const [months, setMonths] = useState(1);
  const [method, setMethod] = useState<Method>('card');
  const [step, setStep] = useState<Step>(start.mode === 'change' ? 'warn' : start.mode === 'renew' ? 'period' : 'plan');
  const [annulled, setAnnulled] = useState(false);
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState('');
  const [cool, setCool] = useState(0);
  const [checkout, setCheckout] = useState<{ id: string | null; url: string | null }>({ id: null, url: null });
  const [creating, setCreating] = useState(false);
  useEffect(() => { if (cool <= 0) return; const t = window.setTimeout(() => setCool(cool - 1), 1000); return () => window.clearTimeout(t); }, [cool]);
  const plan = plans.find((x) => x.id === planId)!;
  const price = plan.prices.find((x) => x.months === months)!;
  const m = METHODS.find((x) => x.id === method)!;
  const sub = p.subscription;
  const canTrial = planId === 'plus' && sub.status === 'free' && !sub.trialUsed;
  const labels = start.mode === 'renew' ? [t('pay.stPeriod'), t('pay.stMethod'), t('pay.stPay')] : [t('pay.stPlan'), t('pay.stPeriod'), t('pay.stMethod'), t('pay.stPay')];
  const order: Step[] = start.mode === 'renew' ? ['period', 'method', 'redirect'] : ['plan', 'period', 'method', 'redirect'];
  const at = order.indexOf(step === 'checking' || step === 'pending' ? 'redirect' : step);

  const doCheck = async () => {
    setStep('checking');
    try { await check.run(checkout.id); setStep('done'); }
    catch (e) {
      const err = e as Error & { code?: string };
      setError(err.message);
      if (err.code === 'pending') { setStep('pending'); setCool(5); } else setStep('failed');
    }
  };
  const goPay = async () => {
    setCreating(true);
    try {
      const r = await profileApi.createPayment({ plan: plan.name, months, price: price.price, method, renew: start.mode === 'renew' });
      setCheckout({ id: r.id, url: r.checkoutUrl });
      setOpened(false);
      setStep('redirect');
    } catch (e) { toast((e as Error).message); } finally { setCreating(false); }
  };
  // the provider's page opens in this tab; it sends the browser back to /profile?payment=<id>
  const openProvider = () => {
    if (checkout.url) { setOpened(true); window.location.assign(checkout.url); return; }
    setOpened(true); toast(t('pay.extDemo', { provider: m.provider }));
  };

  return (
    <div className="pay">
      {at >= 0 && (
        <ol className="pay-steps">{labels.map((l, i) => <li key={l} className={cx(i < at && 'past', i === at && 'on')}><b>{i < at ? <Icon name="check" /> : i + 1}</b>{l}</li>)}</ol>
      )}

      {step === 'warn' && (
        <div className="pay-warn">
          <span className="pay-ok bad"><Icon name="alert" /></span>
          <b>{t('pay.warnTitle')}</b>
          <ul>
            <li>{t('pay.warn1', { plan: sub.plan })}</li>
            <li>{t('pay.warn2', { count: sub.daysLeft })}</li>
            <li>{t('pay.warn3')}</li>
            <li>{t('pay.warn4')}</li>
          </ul>
          <p>{t('pay.warnKeep', { plan: sub.plan })}</p>
          <div className="sub-acts">
            <XButton inv label={cancel.pending ? t('pay.annulling') : t('pay.annul')} busy={cancel.pending}
              onClick={async () => { await cancel.run(); setAnnulled(true); setStep('plan'); }} />
            <button type="button" className="stc-link" onClick={closeSheet}>{t('pay.keep', { plan: sub.plan })}</button>
          </div>
        </div>
      )}

      {step === 'plan' && (
        <>
          {annulled && <p className="pay-note"><Icon name="check" />{t('pay.annulled')}</p>}
          <div className="pay-plans">
            {plans.map((x) => (
              <button key={x.id} type="button" className={cx('pay-plan', x.id === planId && 'on')} onClick={() => setPlanId(x.id as 'plus' | 'premium')} aria-pressed={x.id === planId}>
                <span className="pay-radio" aria-hidden="true" />
                <b>{x.name}</b>
                <small>{x.note}</small>
                <span className="pay-from">{t('pay.from', { price: fmtPrice(x.prices[x.prices.length - 1].perMonth) })}</span>
              </button>
            ))}
          </div>
          <PlanFeatures id={planId} />
          {planId === 'plus' && sub.status === 'free' && (
            sub.trialUsed
              ? <p className="sub-note"><Icon name="check" />{t('trial.used')}</p>
              : (
                <button type="button" className="trial-cta" disabled={trial.pending}
                  onClick={async () => { try { await trial.run(); setStep('trial'); } catch (e) { toast((e as Error).message); } }}>
                  <span className="trial-ic"><Icon name="plus" /></span>
                  <span><b>{trial.pending ? t('plans:up.activating') : t('plans:cta.trial', { count: 5 })}</b><small>{t('trial.noteShort')}</small></span>
                  <Icon name="chev" />
                </button>
              )
          )}
          <div className="pay-foot"><span>{plan.name}</span><span /><XButton inv label={t('pay.toPeriod')} onClick={() => setStep('period')} /></div>
          {canTrial && <span className="pay-hint">{t('pay.orPaid')}</span>}
        </>
      )}

      {step === 'period' && (
        <>
          {start.mode === 'renew' && <p className="pay-note"><Icon name="card" />{t('pay.renewNote', { plan: plan.name })}</p>}
          <span className="pay-k">{t('pay.periodK', { plan: plan.name })}</span>
          <div className="pay-periods">
            {plan.prices.map((x) => {
              const full = plan.prices[0].price * x.months;
              const save = Math.round((1 - x.price / full) * 100);
              return (
                <button key={x.months} type="button" className={cx('pay-per', x.months === months && 'on')} onClick={() => setMonths(x.months)} aria-pressed={x.months === months}>
                  <b>{monthsWord(x.months)}</b><span>{fmtPrice(x.price)}</span><small>{t('pay.perMo', { price: fmtPrice(x.perMonth) })}{save > 0 ? ` · −${save}%` : ''}</small>
                </button>
              );
            })}
          </div>
          <div className="pay-foot">
            {start.mode !== 'renew' ? <button type="button" className="stc-link" onClick={() => setStep('plan')}>← {t('pay.stPlan')}</button> : <span />}
            <b>{fmtPrice(price.price)}</b><XButton inv label={t('pay.toMethod')} onClick={() => setStep('method')} />
          </div>
        </>
      )}

      {step === 'method' && (
        <>
          <div className="pay-methods">
            {METHODS.map((x) => (
              <button key={x.id} type="button" className={cx('pay-m', x.id === method && 'on')} onClick={() => setMethod(x.id)} aria-pressed={x.id === method}>
                <span className="pay-radio" aria-hidden="true" /><span className="ic"><Icon name={x.icon} /></span><b>{t(`pay.method.${x.id}.name`)}</b><small>{t(`pay.method.${x.id}.note`)}</small>
              </button>
            ))}
          </div>
          <div className="pay-foot"><button type="button" className="stc-link" onClick={() => setStep('period')}>← {t('pay.stPeriod')}</button><b>{method === 'crypto' ? usdt(price.price) : fmtPrice(price.price)}</b><XButton inv label={t('pay.toPay')} busy={creating} onClick={goPay} /></div>
        </>
      )}

      {step === 'redirect' && (
        <div className="pay-redirect">
          <div className="pay-sum"><div><span>{t('pay.stPlan')}</span><b>{plan.name} · {monthsWord(months)}</b></div><div><span>{t('pay.stMethod')}</span><b>{m.provider}</b></div><div><span>{t('pay.toPayK')}</span><b>{method === 'crypto' ? usdt(price.price) : fmtPrice(price.price)}</b></div></div>
          {!opened ? (
            <div className="pay-ext">
              <span className="ic big"><Icon name={m.icon} /></span>
              <div>
                <b>{t('pay.extTitle', { provider: m.provider })}</b>
                <p>{t('pay.extText', { provider: m.provider })}</p>
                <button type="button" className="sh-open on" onClick={openProvider}>{t('pay.extOpen', { provider: m.provider })}<span aria-hidden="true">↗</span></button>
              </div>
            </div>
          ) : (
            <div className="pay-ext back">
              <span className="ic big"><Icon name="check" /></span>
              <div><b>{t('pay.backTitle', { provider: m.provider })}</b><p>{t('pay.backText')}</p></div>
            </div>
          )}
          <div className="pay-foot"><button type="button" className="stc-link" onClick={() => setStep('method')}>← {t('pay.stMethod')}</button><span /><XButton inv label={t('pay.check')} onClick={doCheck} /></div>
        </div>
      )}

      {step === 'checking' && (
        <div className="pay-state wait"><span className="pay-spin" /><b>{t('pay.checking')}</b><p>{t('pay.checkingText', { provider: m.provider })}</p></div>
      )}
      {step === 'pending' && (
        <div className="pay-state wait">
          <span className="pay-ok wait"><Icon name="refresh" /></span>
          <b>{t('pay.notYet')}</b>
          <p>{error}</p>
          <XButton inv label={cool > 0 ? `${t('pay.checkAgain')} · ${cool}` : t('pay.checkAgain')} onClick={() => { if (cool <= 0) void doCheck(); }} />
          {MOCK_DATA && <small className="pay-hint">{t('pay.demoPause')}</small>}
        </div>
      )}
      {step === 'done' && (
        <div className="pay-state ok">
          <span className="pay-ok"><Icon name="check" /></span>
          <b>{start.mode === 'renew' ? t('pay.renewed') : t('pay.confirmed')}</b>
          <p>{t('pay.activeUntil', { plan: p.subscription.plan, date: ends(p.subscription.endsAt) })}</p>
          <XButton inv label={t('common:action.done')} onClick={closeSheet} />
        </div>
      )}
      {step === 'failed' && (
        <div className="pay-state bad">
          <span className="pay-ok bad"><Icon name="alert" /></span>
          <b>{t('pay.failedTitle')}</b>
          <p>{error}</p>
          <div className="sub-acts"><XButton inv label={t('pay.tryAgain')} onClick={() => setStep('method')} /></div>
        </div>
      )}
      {step === 'trial' && (
        <div className="pay-state ok">
          <span className="pay-ok"><Icon name="check" /></span>
          <b>{t('trial.on')}</b>
          <p>{t('trial.onText')}</p>
          <XButton inv label={t('trial.setup')} onClick={closeSheet} />
        </div>
      )}
    </div>
  );
}

/* ---------- back from the provider ---------- */
/** /profile?payment=<id>: the server's answer for that payment (never the URL's word for it). */
export function usePaymentReturn() {
  const { openSheet } = useSheet();
  return (id: string) => openSheet({ kicker: i18n.t('profile:pay.kicker'), title: i18n.t('profile:pay.returnTitle'), body: <PaymentReturn id={id} />, className: 'pay-sheet' });
}

const RETURN_POLLS = 8;
const RETURN_EVERY_MS = 4000;

function PaymentReturn({ id }: { id: string }) {
  const { t } = useT('profile');
  const tf = useTime();
  const p = useProfile();
  const { closeSheet } = useSheet();
  const check = useProfileAction(profileApi.checkPayment);
  const pay = useOpenPayment();
  const [state, setState] = useState<{ kind: 'checking' | 'done' | 'pending' | 'failed'; text: string }>({ kind: 'checking', text: '' });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let alive = true;
    let timer = 0;
    const run = async (n: number) => {
      try {
        await check.run(id);
        if (alive) setState({ kind: 'done', text: '' });
      } catch (e) {
        if (!alive) return;
        const err = e as Error & { code?: string };
        if (err.code === 'pending' && n + 1 < RETURN_POLLS) { setState({ kind: 'checking', text: err.message }); timer = window.setTimeout(() => void run(n + 1), RETURN_EVERY_MS); return; }
        setState({ kind: err.code === 'pending' ? 'pending' : 'failed', text: err.message });
      }
    };
    void run(0);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [id, round]); // eslint-disable-line react-hooks/exhaustive-deps
  const sub = p.subscription;
  if (state.kind === 'checking') return <div className="pay pay-state wait"><span className="pay-spin" /><b>{t('pay.checking')}</b><p>{state.text || t('pay.returnText')}</p></div>;
  if (state.kind === 'done') return (
    <div className="pay pay-state ok">
      <span className="pay-ok"><Icon name="check" /></span>
      <b>{t('pay.confirmed')}</b>
      <p>{t('pay.activeUntilWeb', { plan: sub.plan, date: sub.endsAt ? tf.dateLong(sub.endsAt) : '—' })}</p>
      <XButton inv label={t('common:action.done')} onClick={closeSheet} />
    </div>
  );
  if (state.kind === 'pending') return (
    <div className="pay pay-state wait">
      <span className="pay-ok wait"><Icon name="refresh" /></span>
      <b>{t('pay.notYet')}</b>
      <p>{state.text}</p>
      <XButton inv label={t('pay.checkAgain')} onClick={() => { setState({ kind: 'checking', text: '' }); setRound((r) => r + 1); }} />
    </div>
  );
  return (
    <div className="pay pay-state bad">
      <span className="pay-ok bad"><Icon name="alert" /></span>
      <b>{t('pay.failedTitle')}</b>
      <p>{state.text}</p>
      <div className="sub-acts"><XButton inv label={t('pay.tryAgain')} onClick={() => {
        closeSheet();
        // a paid plan that is still running is renewed; otherwise a plan is chosen again
        const own = sub.plan === 'Plus' ? 'plus' : sub.plan === 'Premium' ? 'premium' : null;
        pay(own && (sub.status === 'active' || sub.status === 'expiring') ? { mode: 'renew', plan: own } : { mode: 'new' });
      }} /></div>
    </div>
  );
}
